import { Component, computed, input } from '@angular/core';
import { LucideLoaderCircle, LucideDynamicIcon } from '@lucide/angular';
import { NgpButton } from 'ng-primitives/button';
import { JoiiIcon } from '../../icons/lucide/icon';

export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'positive-filled'
  | 'positive-outlined'
  | 'negative-filled'
  | 'negative-outlined'
  | 'absolute-black-filled'
  | 'absolute-black-outlined'
  | 'absolute-white-filled'
  | 'absolute-white-outlined'
  | 'destructive-filled'
  | 'destructive-outlined'
  | 'success-filled'
  | 'success-outlined'
  | 'neutral-outlined';

@Component({
  selector: 'button[joii-button]',
  imports: [LucideDynamicIcon],
  hostDirectives: [{ directive: NgpButton, inputs: ['disabled'] }],
  template: `
    @if (loading()) {
      <svg class="loadingIcon" [lucideIcon]="spinnerIcon" [size]="iconSize()" />
    }
    @if (leadingIcon() && !loading()) {
      <svg [lucideIcon]="leadingIcon() ?? null" [size]="iconSize()" />
    }
    <ng-content select="[slot=leading]" />

    <div #content class="content">
      <ng-content select="[slot=icon]" />
      @if (icon(); as icon) {
        <svg [lucideIcon]="icon" [size]="iconSize()" />
      }
      <ng-content />
    </div>

    @if (trailingIcon() && !loading()) {
      <svg [lucideIcon]="trailingIcon() ?? null" [size]="iconSize()" />
    }
    <ng-content select="[slot=trailing]" />
  `,
  host: {
    '[attr.data-size]': 'size()',
    '[attr.data-variant]': 'variant()',
    '[attr.data-loading]': 'loading()',
    '[class.icon-only]': 'iconOnly()',
    '[class.no-wrap]': 'noWrap()',
  },
  styleUrl: './button.component.css',
})
export class ButtonComponent {
  readonly size = input<ButtonSize>('md');
  readonly variant = input<ButtonVariant>('primary');
  readonly loading = input<boolean>(false);
  readonly iconOnly = input<boolean>(false);
  readonly leadingIcon = input<JoiiIcon>();
  readonly icon = input<JoiiIcon>();
  readonly trailingIcon = input<JoiiIcon>();
  readonly noWrap = input<boolean>(false);
  spinnerIcon = LucideLoaderCircle;

  iconSize = computed(() => {
    const size = this.size();
    const sizeMap: Record<ButtonSize, number> = {
      sm: 20,
      md: 24,
      lg: 28,
    };
    return sizeMap[size];
  });
}
